import Student from '../../../model/Student';
import {
	Checkbox,
	FormControl,
	IconButton,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CustomTable from '../CustomTable';
import { useContext, useEffect, useRef, useState } from 'react';
import { getFirstStageStudents, updateFirstStageResult } from '../../../api/content-api';
import { loginPage } from '../../../store/fixed-routing';
import AuthContext from '../../../store/auth-context';
import SnackbarContext from '../../../store/snackbar-context';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../ui/Loader';
import ActionButtons from './ActionButtons';
import OlympicsContext from '../../../store/olympics-context';
import { DEFAULT_API_ERROR, DEFAULT_AUTH_ERROR } from '../../../utils/auth-tools';
import { decodeBase64Utf8 } from '../../../utils/string-tools';
import { filter } from '../StagesTable';
import ContactLinks from '../ContactLinks';
import ProtocolVerificationDialog from './ProtocolVerificationDialog';

const FirstStageTable = () => {
	const [allStudents, setAllStudents] = useState([]);
	const [displayedStudents, setDisplayedStudents] = useState<Student[]>([]);
	const [editedStudent, setEditedStudent] = useState<Student>();
	const [isLoading, setIsLoading] = useState(true);
	const [isUpdating, setIsUpdating] = useState(false);
	const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
	const { logout, token, isAdmin, isSecretary, id } = useContext(AuthContext);
	const { olympics, districtFilterId, searchFilterValue, refreshTablesTrigger, setIsRefreshing } =
		useContext(OlympicsContext);
	const isRefreshingRef = useRef(false);
	const { setMsg } = useContext(SnackbarContext);
	const navigate = useNavigate();
	const editable = olympics && (isAdmin || !olympics?.firstStagePublished);

	const getStatusOptionsLabel = (key: string) => {
		switch (key) {
			case 'delivered':
				return 'Dostarczono';
			case 'not-delivered':
				return 'Nie dostarczono';
			default:
				return '';
		}
	};

	const filterStudents = (list: Student[]) => {
		let filtered = filter(list, districtFilterId);

		if (searchFilterValue) {
			filtered = filtered.filter((s) => {
				const matchesSearch = (value: string | undefined) =>
					value && value.toLowerCase().includes(searchFilterValue.toLowerCase());

				return (
					matchesSearch(s.firstName) ||
					matchesSearch(s.lastName) ||
					matchesSearch(s.email) ||
					matchesSearch(s.school?.name) ||
					matchesSearch(s.district?.name) ||
					s.teachers?.some((t: any) => matchesSearch(t.firstName) || matchesSearch(t.lastName))
				);
			});
		}

		return filtered;
	};

	useEffect(() => {
		setDisplayedStudents(filterStudents(allStudents));
	}, [districtFilterId, searchFilterValue, allStudents]);

	const downloadData = (isRefresh = false) => {
		if (isRefresh) {
			isRefreshingRef.current = true;
			setIsRefreshing(true);
		} else {
			setIsLoading(true);
		}
		getFirstStageStudents(token)
			.then((response) => {
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setAllStudents(students);
					setDisplayedStudents(filterStudents(students));
				} else {
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			})
			.catch((exception) => {
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: DEFAULT_AUTH_ERROR });
				} else {
					const msg = exception?.response?.data?.message ? exception?.response?.data?.message : DEFAULT_API_ERROR;
					setMsg({ msg });
				}
				setIsLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			});
	};

	const handleSaveFirstStageResult = () => {
		setIsUpdating(true);
		const data = {
			id: editedStudent?.id,
			firstStageStatus: editedStudent?.result.firstStageStatus,
			firstStagePoints: editedStudent?.result.firstStagePoints,
			firstStageQualified: editedStudent?.result.firstStageQualified,
		};
		updateFirstStageResult(token, data)
			.then((response) => {
				setIsUpdating(false);
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setAllStudents(students);
					setDisplayedStudents(filterStudents(students));
					setEditedStudent(undefined);
					setMsg({ msg: 'Dane ucznia zostały zaktualizowane', severity: 'success' });
					const warningEncoded = response.headers?.['x-olympics-warning-b64'];
					const warningFallback = response.headers?.['x-olympics-warning'];
					if (warningEncoded || warningFallback) {
						const warning = warningEncoded ? decodeBase64Utf8(warningEncoded) : warningFallback;
						setMsg({ msg: warning, severity: 'warning' });
					}
				} else {
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
			})
			.catch((exception) => {
				setIsUpdating(false);
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: DEFAULT_AUTH_ERROR });
				} else {
					const msg = exception?.response?.data?.message ? exception?.response?.data?.message : DEFAULT_API_ERROR;
					setMsg({ msg });
				}
				setIsLoading(false);
			});
	};

	useEffect(() => {
		if (refreshTablesTrigger === 0) {
			downloadData(false);
		} else {
			downloadData(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshTablesTrigger, token]);

	const handleStatusChange = (event: SelectChangeEvent) => {
		if (editedStudent) {
			setEditedStudent({
				...editedStudent,
				result: { ...editedStudent.result, firstStageStatus: event.target.value },
			});
		}
	};

	const handlePointsChange = (event: any) => {
		if (editedStudent) {
			setEditedStudent({
				...editedStudent,
				result: { ...editedStudent.result, firstStagePoints: Number(event.target.value) },
			});
		}
	};

	const handleQualifiedChange = () => {
		if (editedStudent) {
			setEditedStudent({
				...editedStudent,
				result: { ...editedStudent.result, firstStageQualified: !editedStudent.result.firstStageQualified },
			});
		}
	};

	const renderStatusField = (i: Student) => {
		const hasResultRecord = i.result?.firstStageStatus && i.result.firstStageStatus.trim() !== '';
		const isApproved = i.approved === true;
		const isEditing = editedStudent?.id === i.id;

		// Jeśli edytujemy, zawsze pokazuj pole edycyjne
		if (isEditing) {
			return (
				<FormControl
					variant='standard'
					sx={{
						width: '100%',
						fontSize: '16px',
						'.MuiSelect-standard': {
							fontSize: '16px',
						},
					}}
				>
					<Select
						value={editedStudent?.result?.firstStageStatus || ''}
						onChange={handleStatusChange}
					>
						<MenuItem
							value={'delivered'}
							sx={{ fontSize: '16px' }}
						>
							{getStatusOptionsLabel('delivered')}
						</MenuItem>
						<MenuItem
							value={'not-delivered'}
							sx={{ fontSize: '16px' }}
						>
							{getStatusOptionsLabel('not-delivered')}
						</MenuItem>
					</Select>
				</FormControl>
			);
		}

		// 1. Niepotwierdzony + brak rekordu = "Nie wprowadzony"
		if (!isApproved && !hasResultRecord) {
			return (
				<Typography
					variant='caption'
					fontWeight='medium'
				>
					Nie wprowadzony
				</Typography>
			);
		}

		// 2. Niepotwierdzony + jest rekord = status + "Uczeń niepotwierdzony!" na czerwono
		if (!isApproved && hasResultRecord) {
			const statusLabel = getStatusOptionsLabel(i.result.firstStageStatus);
			return (
				<Typography
					variant='caption'
					fontWeight='medium'
					sx={{ color: 'error.main' }}
				>
					{statusLabel ? `${statusLabel} - Uczeń niepotwierdzony!` : 'Uczeń niepotwierdzony!'}
				</Typography>
			);
		}

		// 3. Potwierdzony + brak rekordu = "Potwierdzony, brak wyniku"
		if (isApproved && !hasResultRecord) {
			return (
				<Typography
					variant='caption'
					fontWeight='medium'
				>
					Potwierdzony, brak wyniku
				</Typography>
			);
		}

		// 4. Potwierdzony + jest rekord = normalne wyświetlanie
		return (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{getStatusOptionsLabel(i.result.firstStageStatus)}
			</Typography>
		);
	};

	const renderPointsField = (i: Student) => {
		const hasResultRecord = i.result?.firstStageStatus && i.result.firstStageStatus.trim() !== '';
		const isApproved = i.approved === true;
		const isEditing = editedStudent?.id === i.id;

		// Jeśli edytujemy, zawsze pokazuj pole edycyjne
		if (isEditing) {
			return (
				<TextField
					variant={'standard'}
					type={'number'}
					value={editedStudent.result.firstStagePoints || 0}
					sx={{
						width: '100%',
						height: '100%',
						justifyContent: 'end',
						'& input': {
							pl: '7%',
							minHeight: '100%',
							margin: 0,
							fontSize: '16px',
							textAlign: 'center',
						},
					}}
					onChange={handlePointsChange}
				/>
			);
		}

		// 1. Niepotwierdzony + brak rekordu = "-"
		if (!isApproved && !hasResultRecord) {
			return (
				<Typography
					variant='caption'
					fontWeight='medium'
				>
					-
				</Typography>
			);
		}

		// 2. Niepotwierdzony + jest rekord = wartość na czerwono
		if (!isApproved && hasResultRecord) {
			return (
				<Typography
					variant='caption'
					fontWeight='medium'
					sx={{ color: 'error.main' }}
				>
					{i.result.firstStagePoints}
				</Typography>
			);
		}

		// 3. Potwierdzony + brak rekordu = "-"
		if (isApproved && !hasResultRecord) {
			return (
				<Typography
					variant='caption'
					fontWeight='medium'
				>
					-
				</Typography>
			);
		}

		// 4. Potwierdzony + jest rekord = normalne wyświetlanie
		return (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{i.result.firstStagePoints}
			</Typography>
		);
	};

	const renderQualifiedField = (i: Student) => {
		const hasResultRecord = i.result?.firstStageStatus && i.result.firstStageStatus.trim() !== '';
		const isApproved = i.approved === true;
		const isEditing = editedStudent?.id === i.id;

		// Jeśli edytujemy, zawsze pokazuj checkbox
		if (isEditing) {
			const value = editedStudent.result.firstStageQualified || false;
			return (
				<Checkbox
					checked={value}
					onChange={handleQualifiedChange}
				/>
			);
		}

		// 1. Niepotwierdzony + brak rekordu = "-"
		if (!isApproved && !hasResultRecord) {
			return (
				<Typography
					variant='caption'
					fontWeight='medium'
				>
					-
				</Typography>
			);
		}

		// 2. Niepotwierdzony + jest rekord = wartość na czerwono (jako tekst)
		if (!isApproved && hasResultRecord) {
			return (
				<Typography
					variant='caption'
					fontWeight='medium'
					sx={{ color: 'error.main' }}
				>
					{i.result.firstStageQualified ? 'Tak' : 'Nie'}
				</Typography>
			);
		}

		// 3. Potwierdzony + brak rekordu = "-"
		if (isApproved && !hasResultRecord) {
			return (
				<Typography
					variant='caption'
					fontWeight='medium'
				>
					-
				</Typography>
			);
		}

		// 4. Potwierdzony + jest rekord = normalne wyświetlanie
		return (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{i.result.firstStageQualified ? 'Tak' : 'Nie'}
			</Typography>
		);
	};

	const data = (input: any) => {
		const contactColumns = isSecretary ? [{ header: 'Kontakt', accessor: 'contact', align: 'left' }] : [];
		let additionalColumns: any[] = [];

		if (isAdmin || isSecretary) {
			additionalColumns = [
				{ header: 'Imię', accessor: 'firstName', align: 'left' },
				{ header: 'Nazwisko', accessor: 'lastName', align: 'left' },
			];
		}

		return {
			columns: [
				{ header: 'Lp.', accessor: 'index', align: 'left', width: '70px' },
				{ header: 'Identyfikator', accessor: 'id', align: 'center', width: '150px' },
				{ header: 'Tytuł', accessor: 'personalTitle', align: 'left', width: '100px' },
				...additionalColumns,
				...contactColumns,
				{ header: 'Szkoła', accessor: 'school', align: 'left' },
				{ header: 'Okręg', accessor: 'district', align: 'left', width: '140px' },
				{ header: 'Nauczyciel', accessor: 'teachers', align: 'left', width: '160px' },
				{ header: 'Klasa', accessor: 'schoolClass', width: '100px', align: 'center' },
				{ header: 'Specjalizacja', accessor: 'specialization', width: '100px', align: 'center' },
				{ header: 'Status pracy', accessor: 'status', width: '180px', align: 'center', editable: editable },
				{ header: 'Punkty', accessor: 'points', width: '100px', align: 'center', editable: editable },
				{ header: 'Zakwalifikowany', accessor: 'qualified', width: '170px', align: 'center', editable: editable },
				{ header: '', accessor: 'action', align: 'center', width: '100px', nosort: true, editable: editable },
			],

			rows: input
				? input.map((i: Student, index: number) => {
						return {
							index: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{index + 1}
								</Typography>
							),
							id: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.district.id.toString().padStart(4, '0') + '-' + i.id.toString().padStart(6, '0')}
								</Typography>
							),
							personalTitle: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.personalTitle}
								</Typography>
							),
							contact: isSecretary && <ContactLinks student={i} />,
							firstName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.firstName}
								</Typography>
							),
							lastName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.lastName}
								</Typography>
							),
							school: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.school.name}, {i.school.cityWithCountry}
								</Typography>
							),
							district: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.district?.name || ''}
								</Typography>
							),
							teachers: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{(i.teachers || [])
										.map((t) => `${t.firstName} ${t.lastName}`.trim())
										.filter(Boolean)
										.join(', ')}
								</Typography>
							),
							schoolClass: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.schoolClassText && i.schoolClassText.trim() !== '' ? i.schoolClassText : String(i.schoolClass)}
								</Typography>
							),
							specialization: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.specialization.name}
								</Typography>
							),
							status: renderStatusField(i),
							points: renderPointsField(i),
							qualified: renderQualifiedField(i),
							action: (
								<ActionButtons
									student={i}
									disabled={!editable}
									editedStudent={editedStudent}
									inProgress={isUpdating}
									handleSave={handleSaveFirstStageResult}
									handleClose={() => setEditedStudent(undefined)}
									handleEdit={() => {
										// Upewnij się, że result ma domyślne wartości jeśli nie ma rekordu
										const studentToEdit = { ...i };
										if (!studentToEdit.result || !studentToEdit.result.firstStageStatus) {
											studentToEdit.result = {
												...studentToEdit.result,
												firstStageStatus: '',
												firstStagePoints: 0,
												firstStageQualified: false,
											};
										}
										setEditedStudent(studentToEdit);
									}}
								/>
							),
						};
					})
				: [],
		};
	};
	const footerButtons = isAdmin ? (
		<Tooltip title='Weryfikacja protokołu (Etap I)'>
			<IconButton
				size='small'
				sx={{ color: 'white' }}
				onClick={() => setVerificationDialogOpen(true)}
			>
				<FileUploadIcon fontSize='small' />
			</IconButton>
		</Tooltip>
	) : null;

	return (
		<>
			{isLoading ? (
				<Loader />
			) : (
				<CustomTable
					data={data(displayedStudents)}
					footerLeft={footerButtons}
				/>
			)}
			<ProtocolVerificationDialog
				open={verificationDialogOpen}
				onClose={() => setVerificationDialogOpen(false)}
				stageKey='I'
				title='Weryfikacja protokołu (Etap I)'
				students={displayedStudents}
			/>
		</>
	);
};

export default FirstStageTable;
